import graphql from "babel-plugin-relay/macro";
import { useMemo } from "react";
import { PreloadedQuery, usePreloadedQuery } from "react-relay";

import {
  NotificationChannel,
  useNotificationsUpdateModalDataQuery,
} from "./__generated__/useNotificationsUpdateModalDataQuery.graphql";

export type NotificationPreferencesChannels = {
  bookingUpdates: NotificationChannel[];
  bookingReminders: NotificationChannel[];
  requestedTimeAvailable: NotificationChannel[];
  eventUpdates: NotificationChannel[];
  eventReminders: NotificationChannel[];
};

function sanitizeChannels(
  channels?: ReadonlyArray<NotificationChannel | null | undefined>
): NotificationChannel[] {
  if (!channels) return [];
  return Array.from(
    new Set(channels.filter((c): c is NotificationChannel => c != null))
  );
}

export const useNotificationsUpdateModalData = (
  queryRef: PreloadedQuery<useNotificationsUpdateModalDataQuery>
) => {
  const data = usePreloadedQuery<useNotificationsUpdateModalDataQuery>(
    NotificationsUpdateModalInnerQueryString,
    queryRef
  );

  const memberUser = data?.session?.user;

  const preferences: NotificationPreferencesChannels = useMemo(
    () => ({
      bookingUpdates: sanitizeChannels(
        memberUser?.notificationPreferencesChannels?.bookingUpdates
      ),
      bookingReminders: sanitizeChannels(
        memberUser?.notificationPreferencesChannels?.bookingReminders
      ),
      requestedTimeAvailable: sanitizeChannels(
        memberUser?.notificationPreferencesChannels?.requestedTimeAvailable
      ),
      eventUpdates: sanitizeChannels(
        memberUser?.notificationPreferencesChannels?.eventUpdates
      ),
      eventReminders: sanitizeChannels(
        memberUser?.notificationPreferencesChannels?.eventReminders
      ),
    }),
    [
      memberUser?.notificationPreferencesChannels?.bookingUpdates,
      memberUser?.notificationPreferencesChannels?.bookingReminders,
      memberUser?.notificationPreferencesChannels?.requestedTimeAvailable,
      memberUser?.notificationPreferencesChannels?.eventUpdates,
      memberUser?.notificationPreferencesChannels?.eventReminders,
    ]
  );

  return {
    contactEmail: memberUser?.email ?? "",
    contactPhone: memberUser?.phoneNumber?.formattedNumber ?? "",
    isEmailVerified: memberUser?.isEmailVerified ?? false,
    isPhoneVerified: memberUser?.isPhoneVerified ?? false,
    userId: memberUser?.id ?? "",
    preferences,
  };
};

export const NotificationsUpdateModalInnerQueryString = graphql`
  query useNotificationsUpdateModalDataQuery {
    session {
      user {
        id
        ... on MemberUser {
          email
          isEmailVerified
          isPhoneVerified
          phoneNumber {
            formattedNumber(format: INTERNATIONAL)
          }
          notificationPreferencesChannels {
            bookingUpdates
            bookingReminders
            eventUpdates
            eventReminders
            requestedTimeAvailable
          }
        }
      }
    }
  }
`;
